import React, { useState, useEffect, useRef } from 'react';
import ZoomImageViewer from './ZoomImageViewer';
import { getAccessToken, refreshAccessToken } from '../services/apiService';
import { SERVER_URL } from '../config/serverConfig';

/**
 * Lazy-Thumbnail eines Paket-Fotos (server-proxied, pro User abgesichert).
 * Einmal tippen öffnet das Original (komprimiert) als Vollbild — nochmal tippen
 * schließt es wieder. Zeigt nichts, wenn das Paket kein Bild hat.
 */
// 🗃️ Session-Cache der Thumbnail-Object-URLs (packageId → blob-URL). Verhindert das Blinken:
// wenn die Liste beim Sync die Items kurz neu aufbaut, ist das Bild sofort wieder da statt
// erneut geladen zu werden. Thumb-URLs werden bewusst NICHT revoked (bleiben die Session gültig).
const thumbCache = new Map();

async function fetchPackageImage(packageId, size) {
  const url = `${SERVER_URL}/packages/${encodeURIComponent(packageId)}/image?size=${size}`;
  let token = getAccessToken();
  let r = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (r.status === 401) {
    try { await refreshAccessToken(); token = getAccessToken(); } catch { /* */ }
    r = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  }
  if (!r.ok) throw new Error('img ' + r.status);
  return URL.createObjectURL(await r.blob());
}

export default function PackageThumbnail({ packageId }) {
  // Schon geladen? → sofort aus dem Cache zeigen (kein Blinken bei Listen-Neuaufbau)
  const [src, setSrc] = useState(() => thumbCache.get(packageId) || null);
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(() => thumbCache.has(packageId));
  const [full, setFull] = useState(null);
  const containerRef = useRef(null);

  // Lazy: erst laden, wenn (fast) im Sichtbereich
  useEffect(() => {
    if (visible) return;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const obs = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting)) { setVisible(true); obs.disconnect(); }
    }, { rootMargin: '200px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible || !packageId) return;
    // Bereits im Cache → sofort setzen, nichts nachladen (verhindert das wiederholte Blinken)
    if (thumbCache.has(packageId)) { setSrc(thumbCache.get(packageId)); return; }
    let cancelled = false;
    fetchPackageImage(packageId, 'thumb')
      .then((u) => { thumbCache.set(packageId, u); if (!cancelled) setSrc(u); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [visible, packageId]);

  // Einmal tippen = groß (Tap nicht ans Paket weiterreichen → keine Auswahl)
  const openFull = async (e) => {
    if (e) e.stopPropagation();
    try { const u = await fetchPackageImage(packageId, 'full'); setFull(u); } catch { /* */ }
  };
  const closeFull = (e) => {
    if (e) e.stopPropagation();
    try { URL.revokeObjectURL(full); } catch { /* */ }
    setFull(null);
  };

  // 📱 Touch: der Karten-Swipe/-Tap schluckt sonst den Klick aufs Bild → Vollbild ging nur am PC.
  // Wir erkennen einen echten Tap (kaum Bewegung) direkt am Bild und öffnen dann selbst.
  const tapRef = useRef(null);
  const onImgTouchStart = (e) => {
    e.stopPropagation(); // kein Karten-Swipe/-Regaloverlay beim Bild-Tap
    const t = e.touches[0];
    tapRef.current = t ? { x: t.clientX, y: t.clientY, t: Date.now() } : null;
  };
  const onImgTouchEnd = (e) => {
    e.stopPropagation();
    const s = tapRef.current; tapRef.current = null;
    if (!s) return;
    const t = e.changedTouches[0];
    const moved = t ? Math.hypot(t.clientX - s.x, t.clientY - s.y) : 0;
    if (moved < 14 && Date.now() - s.t < 700) { e.preventDefault(); openFull(); } // echter Tap → Vollbild
  };

  if (failed) return null; // kein Bild → nichts anzeigen

  // Volle Kartenbreite (bricht als eigene Zeile um) + feste Höhe: großes Vorschaubild statt
  // schmaler Streifen, und Platzhalter == Bild-Höhe → keine Layout-Sprünge beim Laden.
  return (
    <div ref={containerRef} style={{ marginTop: 10, height: 200, width: '100%', flexBasis: '100%' }}>
      {src ? (
        <img src={src} alt="Paketfoto"
          onClick={openFull} onTouchStart={onImgTouchStart} onTouchEnd={onImgTouchEnd}
          title="Tippen = groß"
          style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8,
            background: '#000', cursor: 'zoom-in', display: 'block', touchAction: 'manipulation' }} />
      ) : (
        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.4 }}>⏳</div>
      )}
      {/* 🔍 Vollbild mit Pinch-Zoom (Portal → Karten-Swipe stört nicht) */}
      {full && <ZoomImageViewer src={full} onClose={() => closeFull()} />}
    </div>
  );
}
